import React from "react";
import { Link, withRouter } from "react-router-dom";
import BluestuffLogo from "./Common/BluestuffLogo";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { SidebarData } from "./SidebarData";
import { setOpenTabs, tabManager } from "../components/sidebar/sidebarSlice";
import { setOpen } from "./AppSlice";
import { User, Users, Smile, Gift, UserCog, DollarSign, Settings, ShieldAlert, MessageSquare, UserCircle, ChevronLeft, ChevronRight, Search, X, TrendingUp } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { ScrollArea } from "../ui/scroll-area";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";

// Icon mapping from material-icons to lucide-react
const iconMap = {
  'person': User,
  'people_outline': Users,
  'tag_faces': Smile,
  'card_giftcard': Gift,
  'manage_accounts': UserCog,
  'attach_money': DollarSign,
  'settings': Settings,
  'gpp_maybe': ShieldAlert,
  'message': MessageSquare,
  'man': UserCircle,
  'trending_up': TrendingUp,
};

// Per-section accent colors (bg, text, active-bg classes)
const sectionColors = {
  'PLAYERS': { pill: 'bg-blue-100 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400', active: 'bg-blue-600' },
  'OPERATIONS': { pill: 'bg-violet-100 dark:bg-violet-950/50 text-violet-600 dark:text-violet-400', active: 'bg-violet-600' },
  'TAGGING': { pill: 'bg-teal-100 dark:bg-teal-950/50 text-teal-600 dark:text-teal-400', active: 'bg-teal-600' },
  'BONUSES': { pill: 'bg-pink-100 dark:bg-pink-950/50 text-pink-600 dark:text-pink-400', active: 'bg-pink-600' },
  'PAYMENTS': { pill: 'bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400', active: 'bg-emerald-600' },
  'AFFILIATE': { pill: 'bg-orange-100 dark:bg-orange-950/50 text-orange-600 dark:text-orange-400', active: 'bg-orange-600' },
  'RISK MANAGEMENT': { pill: 'bg-red-100 dark:bg-red-950/50 text-red-600 dark:text-red-400', active: 'bg-red-600' },
  'CONFIGURATION': { pill: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400', active: 'bg-slate-600' },
  'COMMUNICATION': { pill: 'bg-indigo-100 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400', active: 'bg-indigo-600' },
  'USER MANAGEMENT': { pill: 'bg-cyan-100 dark:bg-cyan-950/50 text-cyan-600 dark:text-cyan-400', active: 'bg-cyan-600' },
};

const getColors = (title) => sectionColors[title?.toUpperCase()] || { pill: 'bg-primary/10 text-primary', active: 'bg-primary' };
const getIconComponent = (iconName) => iconMap[iconName] || User;

class SubMenu extends React.Component {
  constructor(props) {
    super(props);
    this.state = { activeSubMenus: {}, searchTerm: "" };
  }

  handleSearchChange = (value) => {
    this.setState({ searchTerm: value });
  };

  clearSearch = () => {
    this.setState({ searchTerm: "" });
  };

  handleSettingTabs(item, isSuperSub = false) {
    this.props.dispatch(tabManager(item.subtitle, this.props.tabStack, null, null, isSuperSub));
  }

  handleAccordionChange = (value) => {
    this.props.dispatch(setOpenTabs(value ? [value] : []));
  };

  handleSubMenuToggle = (index) => {
    this.setState(prevState => ({
      activeSubMenus: { ...prevState.activeSubMenus, [index]: !prevState.activeSubMenus[index] }
    }));
  };

  onClose() {
    this.props.dispatch(setOpen(!this.props.open));
  }

  isPathActive(subItem) {
    const currentPath = this.props.location?.pathname || window.location.pathname;
    if (currentPath === subItem.path) return true;
    if (subItem.path === '/bidashboard' && currentPath.startsWith('/bidashboard/')) return true;
    if (subItem.path === '/bireports' && currentPath.startsWith('/bireports/')) return true;
    if (subItem.path === '/vip-reports' && currentPath.startsWith('/vip-reports/')) return true;
    return false;
  }

  render() {
    let givenPermissions = this.props.userPagePermissions.map((value) => value.permissionName);
    const activeValue = this.props.openTabs.length > 0 ? this.props.openTabs[0] : "";
    const isCollapsed = !this.props.open;

    // Instant page search - flatten every permitted, visible page and match on title.
    const query = this.state.searchTerm.trim().toLowerCase();
    const isSearching = !isCollapsed && query.length > 0;
    const searchResults = isSearching
      ? SidebarData.flatMap((section) =>
        section.subNav
          .filter(
            (subItem) =>
              subItem.isVisible &&
              givenPermissions.includes(subItem.permissionName) &&
              subItem.subtitle.toLowerCase().includes(query)
          )
          .map((subItem) => ({
            ...subItem,
            sectionTitle: section.title,
            sectionIcon: section.icon,
          }))
      )
      : [];

    return (
      <div className={`h-full flex flex-col bg-card border-r border-border shadow-sm transition-all duration-300 ease-in-out ${isCollapsed ? "w-[68px]" : "w-[260px]"} overflow-hidden`}>

        {/* Header */}
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-4 py-4 border-b border-border bg-card shrink-0`}>
          {!isCollapsed && (
            <Link to="/dashboardV2" className="flex items-center gap-2 min-w-0 pl-2">
              <BluestuffLogo className="h-6 w-auto shrink-0" />
            </Link>
          )}
          <button
            className="flex items-center justify-center w-8 h-8 rounded-md border border-border bg-background hover:bg-accent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 shadow-sm transition-all duration-150 shrink-0"
            onClick={() => this.onClose()}
            title={isCollapsed ? "Expand" : "Collapse"}
          >
            {isCollapsed
              ? <ChevronRight size={15} strokeWidth={2.5} />
              : <ChevronLeft size={15} strokeWidth={2.5} />
            }
          </button>
        </div>

        {/* Page search */}
        {!isCollapsed && (
          <div className="px-3 pt-3 pb-1 shrink-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search pages..."
                value={this.state.searchTerm}
                onChange={(e) => this.handleSearchChange(e.target.value)}
                className="h-9 pl-8 pr-8 text-sm"
              />
              {this.state.searchTerm && (
                <button
                  type="button"
                  onClick={this.clearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Menu */}
        <ScrollArea className="flex-1 min-h-0">
          {isSearching ? (
            <div className="py-2 px-3 space-y-1">
              {searchResults.length > 0 ? (
                searchResults.map((result, index) => {
                  const ResultIcon = getIconComponent(result.sectionIcon);
                  const isActive =
                    this.props.activeTab === result.subtitle || this.isPathActive(result);
                  return (
                    <Link
                      key={`${result.permissionName}-${index}`}
                      to={result.path}
                      onClick={() => {
                        this.handleSettingTabs(result);
                        this.clearSearch();
                      }}
                    >
                      <div
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-md transition-colors ${isActive
                          ? 'bg-primary text-primary-foreground font-medium'
                          : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                          }`}
                      >
                        <span className="flex items-center justify-center w-7 h-7 rounded-md bg-muted text-slate-600 dark:text-slate-300 shrink-0">
                          <ResultIcon size={15} />
                        </span>
                        <span className="flex flex-col min-w-0">
                          <span className="truncate text-[13px]">{result.subtitle}</span>
                          <span className="truncate text-[10px] uppercase tracking-wide opacity-70">
                            {result.sectionTitle}
                          </span>
                        </span>
                      </div>
                    </Link>
                  );
                })
              ) : (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  No pages found
                </div>
              )}
            </div>
          ) : (
          <div className={`py-4 ${isCollapsed ? 'px-2' : 'px-3'}`}>
            <Accordion
              type="single"
              collapsible
              value={activeValue}
              onValueChange={this.handleAccordionChange}
              className="w-full space-y-1.5"
            >
              {SidebarData.length > 0 &&
                Object.keys(this.props.userPagePermissions).length > 0 &&
                givenPermissions.length > 0 &&
                SidebarData.filter((item) => {
                  for (let i = 0; i < item.children().length; i++) {
                    if (givenPermissions.includes(item.children()[i])) return true;
                  }
                  return false;
                }).map((item, index) => {
                  const colors = getColors(item.title);
                  const IconComponent = getIconComponent(item.icon);
                  const isOpen = activeValue !== "" && activeValue === item.title;
                  const notificationCount =
                    item.title === "PAYMENTS" && Object.keys(this.props.notifications).length > 0 && this.props.notifications.totalCount > 0
                      ? this.props?.notifications?.pendingDepositCount + this.props?.notifications?.pendingWithdrawals
                      : item.title === "AFFILIATE" && Object.keys(this.props.notifications).length > 0 && this.props.notifications.totalCount > 0
                        ? this.props?.notifications?.pendingAffiliateUserRequestCount
                        : 0;

                  // Collapsed: icon-only pill
                  if (isCollapsed) {
                    return (
                      <div
                        key={item.id}
                        className={`relative flex items-center justify-center w-full h-11 rounded-md cursor-pointer transition-colors ${isOpen ? colors.pill + ' ring-1 ring-inset ring-current/20' : 'hover:bg-accent'}`}
                        title={item.title}
                      >
                        <IconComponent size={19} />
                        {notificationCount > 0 && (
                          <span className="absolute top-0.5 right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold">
                            {notificationCount > 9 ? '9+' : notificationCount}
                          </span>
                        )}
                      </div>
                    );
                  }

                  return (
                    <AccordionItem key={item.id} value={item.title} className="border-none">
                      <AccordionTrigger
                        showChevron={true}
                        className={`px-3 py-0 h-11 rounded-md text-sm font-medium hover:no-underline hover:bg-accent transition-colors ${isOpen ? 'bg-accent' : ''}`}
                      >
                        <div className="flex items-center gap-2.5 w-full">
                          <span className={`flex items-center justify-center w-8 h-8 rounded-md shrink-0 transition-colors ${isOpen ? colors.pill : 'bg-muted text-slate-600 dark:text-slate-300'}`}>
                            <IconComponent size={16} />
                          </span>
                          <span className="flex-1 text-left text-[12.5px] font-semibold tracking-wide text-slate-600 dark:text-slate-300">{item.title}</span>
                          {notificationCount > 0 && (
                            <Badge variant="destructive" className="h-4 px-1 text-[10px] mr-1">
                              {notificationCount}
                            </Badge>
                          )}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pb-3 pt-1.5">
                        <div className="ml-7 pl-3 border-l-2 border-border space-y-1.5">
                          {item.subNav.map((subItem, subIndex) => {
                            const isActive = this.props.activeTab === subItem.subtitle || this.isPathActive(subItem);
                            const subNotificationCount =
                              ["PENDING DEPOSITS", "PENDING WITHDRAWAL", "PENDING WITHDRAWALS", "BECOME AN AFFILIATE"].includes(subItem.subtitle) &&
                                Object.keys(this.props.notifications).length > 0 &&
                                this.props?.notifications?.totalCount > 0
                                ? subItem.subtitle === "PENDING DEPOSITS"
                                  ? this.props?.notifications?.pendingDepositCount
                                  : subItem.subtitle === "BECOME AN AFFILIATE"
                                    ? this.props?.notifications?.pendingAffiliateUserRequestCount
                                    : this.props?.notifications?.pendingWithdrawals
                                : 0;

                            return (
                              subItem.isVisible &&
                              givenPermissions.includes(subItem.permissionName) && (
                                <Link
                                  key={subIndex}
                                  onClick={() => this.handleSettingTabs(subItem)}
                                  to={subItem.path}
                                >
                                  <div className={`relative flex items-center justify-between px-3 py-2.5 text-[13px] rounded-md transition-colors ${isActive
                                    ? 'bg-primary text-primary-foreground font-medium'
                                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                                    }`}>
                                    {isActive && (
                                      <span className="absolute left-0 top-1 bottom-1 w-0.5 rounded-r-full bg-primary-foreground/50" />
                                    )}
                                    <span className="truncate">{subItem.subtitle}</span>
                                    {subNotificationCount > 0 && (
                                      <Badge variant={isActive ? "outline" : "destructive"} className={`h-4 px-1 text-[10px] shrink-0 ml-1 ${isActive ? 'bg-primary-foreground text-primary border-primary-foreground/30' : ''}`}>
                                        {subNotificationCount}
                                      </Badge>
                                    )}
                                  </div>
                                </Link>
                              )
                            );
                          })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
            </Accordion>
          </div>
          )}
        </ScrollArea>
      </div>
    );
  }
}
function mapStateToProps(state) {
  return {
    open: state.appslice.open,
    userPermission: state.dashboard.userPermission,
    userPagePermissions: state.dashboard.userPagePermissions,
    notifications: state.pendingWithrawal.notifications,
    isAffiliateUser: state.loginSlice.isAffiliateUser,
    isSubAffiliateUser: state.loginSlice.isSubAffiliateUser,
    openTabs: state.sidebar.openTabs,
    activeTab: state.sidebar.activeTab,
    tabStack: state.sidebar.tabStack,
    starbetPermission: state.dashboard.starbetLogoPermission,
  };
}
function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    actions: bindActionCreators({}, dispatch),
  };
}
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SubMenu));